// @ts-nocheck
import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

// ---------------------------------------------------------------------------
// Helper: debounce hook
// Returns a stable debounced version of `fn` that fires after `delay` ms of
// silence. The latest `fn` reference is always used (no stale-closure bugs).
// ---------------------------------------------------------------------------
export function useDebounceCallback(fn, delay = 300) {
  const timerRef = React.useRef(null);
  const fnRef = React.useRef(fn);

  React.useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  React.useEffect(() => {
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  return React.useCallback(
    (...args) => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        fnRef.current(...args);
      }, delay);
    },
    [delay],
  );
}

// ---------------------------------------------------------------------------
// Helper: Convert new Places API "Place" object → legacy PlaceResult shape
// expected by callers:
//   address.address_components[i].long_name / short_name / types
//   address.geometry.location.lat() / .lng()
//   address.formatted_address
//   address.name
// ---------------------------------------------------------------------------
export function normalizePlaceResult(place) {
  const addressComponents = (place.addressComponents || []).map((comp) => ({
    long_name: comp.longText,
    short_name: comp.shortText,
    types: comp.types,
  }));

  return {
    address_components: addressComponents,
    formatted_address: place.formattedAddress,
    name: place.displayName,
    geometry: {
      location: place.location, // LatLng – .lat() / .lng() work as before
    },
    place_id: place.id,
  };
}

// Fields requested when fetching full place details
const DETAIL_FIELDS = [
  "addressComponents",
  "formattedAddress",
  "location",
  "displayName",
  "id",
];

// ---------------------------------------------------------------------------
// Core hook: manual autocomplete using AutocompleteSuggestion (new Places API)
//
// AutocompleteSuggestion replaces the legacy AutocompleteService as of
// March 2025. The key difference is it is promise-based (no callbacks) and
// place details are fetched directly on the suggestion via toPlace().fetchFields().
//
// API calls are debounced so the API is only hit after the user pauses typing.
// ---------------------------------------------------------------------------
export function useManualAutocomplete({
  inputRef,
  onPlaceSelect,
  autocompleteOptions = {},
  debounceDelay = 300,
}) {
  const { isLoaded } = useSelector((store) => store.googleMapsPackage || {});
  const [suggestions, setSuggestions] = React.useState([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const sessionTokenRef = React.useRef(null);

  // Initialise a session token once the Maps API is ready.
  // AutocompleteSuggestion does not need a service instance – it uses a
  // static method – so no service refs are required.
  React.useEffect(() => {
    if (!isLoaded) return;
    const places = window.google?.maps?.places;
    if (places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
  }, [isLoaded]);

  // Rotate the session token after a selection (groups keystrokes + detail
  // fetch into one billing session per Google's recommended practice).
  const refreshSessionToken = React.useCallback(() => {
    const places = window.google?.maps?.places;
    if (places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
  }, []);

  // Helper for retrying async operations
  const retry = async (fn, retries = 3, delay = 500) => {
    try {
      return await fn();
    } catch (error) {
      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return retry(fn, retries - 1, delay);
      }
      throw error;
    }
  };

  // Debounced fetch – the API is NOT called until the user stops typing
  const fetchSuggestions = useDebounceCallback(async (value) => {
    const places = window.google?.maps?.places;

    if (!value || value.length < 2 || !places?.AutocompleteSuggestion) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    try {
      const { suggestions: results } = await retry(() =>
        places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
          sessionToken: sessionTokenRef.current,
          ...autocompleteOptions,
        })
      );

      setSuggestions(results ?? []);
      setIsOpen((results ?? []).length > 0);
      setActiveIndex(-1);
    } catch (err) {
      console.error(
        "[AddressAutocomplete] fetchAutocompleteSuggestions failed after retries:",
        err,
      );
      setSuggestions([]);
      setIsOpen(false);
    }
  }, debounceDelay);

  const handleInputChange = React.useCallback(
    (e) => {
      fetchSuggestions(e.target.value);
    },
    [fetchSuggestions],
  );

  const selectSuggestion = React.useCallback(
    async (suggestion) => {
      // Only place predictions are selectable (not query predictions)
      const placePrediction = suggestion.placePrediction;
      if (!placePrediction) return;

      // Close the dropdown immediately and fill the input
      setIsOpen(false);
      setSuggestions([]);
      if (inputRef.current) {
        inputRef.current.value = placePrediction.text?.toString() ?? "";
      }

      try {
        const place = placePrediction.toPlace();
        await retry(() => place.fetchFields({ fields: DETAIL_FIELDS }));
        onPlaceSelect(normalizePlaceResult(place));
      } catch (err) {
        console.error(
          "[AddressAutocomplete] place.fetchFields failed after retries:",
          err,
        );
      } finally {
        // Always rotate the session token regardless of success/failure
        refreshSessionToken();
      }
    },
    [inputRef, onPlaceSelect, refreshSessionToken],
  );

  // Keyboard navigation
  const handleKeyDown = React.useCallback(
    (e) => {
      if (!isOpen || suggestions.length === 0) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (activeIndex >= 0) selectSuggestion(suggestions[activeIndex]);
      } else if (e.key === "Escape") {
        setIsOpen(false);
      }
    },
    [isOpen, suggestions, activeIndex, selectSuggestion],
  );

  const handleBlur = React.useCallback(() => {
    // Short delay so a mousedown on a suggestion registers before blur closes it
    setTimeout(() => setIsOpen(false), 150);
  }, []);

  return {
    suggestions,
    isOpen,
    activeIndex,
    handleInputChange,
    handleKeyDown,
    handleBlur,
    selectSuggestion,
  };
}

// ---------------------------------------------------------------------------
// Shared dropdown renderer
// ---------------------------------------------------------------------------
const dropdownStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 9999,
  background: "#fff",
  border: "1px solid #ccc",
  borderTop: "none",
  borderRadius: "0 0 4px 4px",
  boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
  maxHeight: 240,
  overflowY: "auto",
  listStyle: "none",
  margin: 0,
  padding: 0,
};

const itemStyle = (isActive) => ({
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: 14,
  background: isActive ? "#d4e0ffff" : "transparent",
  borderBottom: "1px solid #f0f0f0",
});

export function PredictionDropdown({ suggestions, activeIndex, onSelect }) {
  return (
    <ul style={dropdownStyle} role="listbox">
      {suggestions.map((suggestion, idx) => {
        const pred = suggestion.placePrediction;
        const mainText =
          pred?.mainText?.toString() ?? pred?.text?.toString() ?? "";
        const secondaryText = pred?.secondaryText?.toString() ?? "";
        const key = pred?.placeId ?? idx;

        return (
          <li
            key={key}
            role="option"
            aria-selected={idx === activeIndex}
            style={itemStyle(idx === activeIndex)}
            onMouseDown={() => onSelect(suggestion)} // mousedown fires before blur
          >
            <strong style={{ color: "#000" }}>{mainText}</strong>{" "}
            {secondaryText && (
              <span style={{ color: "#888" }}>{secondaryText}</span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// AddressAutocomplete (default export)
// ---------------------------------------------------------------------------
const AddressAutocomplete = ({
  setAddress,
  children,
  readOnly = false,
  debounceDelay = 300,
  inputStyle = {
    background: "transparent",
    border: "1px white solid",
    borderRadius: "5px",
    height: "50px",
    color: "white",
    padding: "10px",
    fontSize: "16px",
    fontWeight: "500",
    fontFamily: "Kanit,sans-serif",
    outline: "none",
  },
  inputClassName = "",
  placeholder = "Enter an address...",
  value,
  defaultValue,
  onChange,
  handleChange,
  handleBlur: onBlurProp,
  name,
  ...otherProps
}) => {
  const { isLoaded } = useSelector((store) => store.googleMapsPackage || {});
  const inputRef = React.useRef(null);
  const {
    suggestions,
    isOpen,
    activeIndex,
    handleInputChange: hookHandleInputChange,
    handleKeyDown,
    handleBlur: hookHandleBlur,
    selectSuggestion,
  } = useManualAutocomplete({
    inputRef,
    onPlaceSelect: setAddress,
    autocompleteOptions: {},
    debounceDelay,
  });

  const handleInputChange = (e) => {
    hookHandleInputChange(e);
    if (handleChange) handleChange(e);
    if (onChange) onChange(e);
  };

  const handleBlur = (e) => {
    hookHandleBlur();
    if (onBlurProp) onBlurProp(e);
  };

  if (readOnly || !isLoaded) {
    return (
      <input
        className={inputClassName}
        style={{ width: "100%", boxSizing: "border-box", ...inputStyle }}
        placeholder={placeholder}
        onChange={handleInputChange}
        onBlur={handleBlur}
        value={value !== undefined ? value : ""}
        defaultValue={defaultValue}
        name={name}
        readOnly={readOnly}
        {...otherProps}
      />
    );
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        className={inputClassName}
        style={{ width: "100%", boxSizing: "border-box", ...inputStyle }}
        placeholder={placeholder}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        role="combobox"
        value={value !== undefined ? value : ""}
        defaultValue={defaultValue}
        name={name}
        {...otherProps}
      />
      {isOpen && suggestions.length > 0 && (
        <PredictionDropdown
          suggestions={suggestions}
          activeIndex={activeIndex}
          onSelect={(suggestion) => {
            selectSuggestion(suggestion);
            const text = suggestion?.placePrediction?.mainText?.toString();
            if (text && name) {
              const e = { target: { name, value: text } };
              if (handleChange) handleChange(e);
              if (onChange) onChange(e);
            }
          }}
        />
      )}
    </div>
  );
};

AddressAutocomplete.propTypes = {
  setAddress: PropTypes.func.isRequired,
  children: PropTypes.node,
  readOnly: PropTypes.bool,
  /** ms of typing silence before the Autocomplete API is called (default 300) */
  debounceDelay: PropTypes.number,
  inputStyle: PropTypes.object,
  inputClassName: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  name: PropTypes.string,
};

// ---------------------------------------------------------------------------
// AddressFilterAutocomplete (named export)
//
// includedPrimaryTypes replaces the legacy `types` filter option used by
// AutocompleteService. Supported values mirror the new Places API type table.
// ---------------------------------------------------------------------------
export const AddressFilterAutocomplete = ({
  setAddress,
  children,
  filterCities = false,
  filterStates = false,
  readOnly = false,
  debounceDelay = 300,
  inputStyle = {
    background: "transparent",
    border: "1px white solid",
    borderRadius: "5px",
    height: "50px",
    color: "white",
    padding: "10px",
    fontSize: "16px",
    fontWeight: "500",
    fontFamily: "Kanit,sans-serif",
    outline: "none",
  },
  inputClassName = "",
  placeholder = "Enter a city or state",
  value,
  defaultValue,
  onChange,
  name,
  onBlur: onBlurProp,
  // fullWidth absorvido aqui: era repassado via {...otherProps} ao <input> nativo
  // (React: "does not recognize the `fullWidth` prop on a DOM element"). O input ja e width:100%.
  fullWidth: _fullWidth,
  ...otherProps
}) => {
  const { isLoaded } = useSelector((store) => store.googleMapsPackage || {});
  const inputRef = React.useRef(null);

  const autocompleteOptions = React.useMemo(() => {
    const includedPrimaryTypes = [];
    if (filterCities) includedPrimaryTypes.push("locality");
    if (filterStates) includedPrimaryTypes.push("administrative_area_level_1");
    return includedPrimaryTypes.length > 0 ? { includedPrimaryTypes } : {};
  }, [filterCities, filterStates]);

  const {
    suggestions,
    isOpen,
    activeIndex,
    handleInputChange: hookHandleInputChange,
    handleKeyDown,
    handleBlur: hookHandleBlur,
    selectSuggestion,
  } = useManualAutocomplete({
    inputRef,
    onPlaceSelect: setAddress,
    autocompleteOptions,
    debounceDelay,
  });

  const handleInputChange = (e) => {
    hookHandleInputChange(e);
    if (onChange) onChange(e);
  };

  const handleBlur = (e) => {
    hookHandleBlur();
    if (onBlurProp) onBlurProp(e);
  };

  if (readOnly || !isLoaded) {
    return (
      <input
        className={inputClassName}
        style={{ width: "100%", boxSizing: "border-box", ...inputStyle }}
        placeholder={placeholder}
        value={value !== undefined ? value : ""}
        defaultValue={defaultValue}
        name={name}
        readOnly={readOnly}
        onChange={handleInputChange}
        onBlur={handleBlur}
        {...otherProps}
      />
    );
  }

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <input
        ref={inputRef}
        className={inputClassName}
        style={{ width: "100%", boxSizing: "border-box", ...inputStyle }}
        placeholder={placeholder}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        role="combobox"
        value={value !== undefined ? value : ""}
        defaultValue={defaultValue}
        name={name}
        {...otherProps}
      />
      {isOpen && suggestions.length > 0 && (
        <PredictionDropdown
          suggestions={suggestions}
          activeIndex={activeIndex}
          onSelect={selectSuggestion}
        />
      )}
    </div>
  );
};

AddressFilterAutocomplete.propTypes = {
  setAddress: PropTypes.func.isRequired,
  children: PropTypes.node,
  filterCities: PropTypes.bool,
  filterStates: PropTypes.bool,
  readOnly: PropTypes.bool,
  /** ms of typing silence before the Autocomplete API is called (default 300) */
  debounceDelay: PropTypes.number,
  inputStyle: PropTypes.object,
  inputClassName: PropTypes.string,
  placeholder: PropTypes.string,
};

export default AddressAutocomplete;